/* eslint-disable */
import { useState } from "react";
import {
  X,
  Upload,
  CheckCircle2,
  AlertTriangle,
  PhoneOff,
  SkipForward,
  FileSpreadsheet,
} from "lucide-react";
import { usersApi } from "../auth/api";
import "./BulkOnboardModal.css";

export default function BulkOnboardModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setUploading(false);
    setResult(null);
    setError("");
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const r = await usersApi.onboardBulk(file, token);
      setResult(r);
      onSuccess && onSuccess();
    } catch (err) {
      setError(err.message || "Bulk onboard failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bulk-overlay" onClick={onClose}>
      <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-head">
          <h2>
            <FileSpreadsheet size={18} /> Bulk onboard from spreadsheet
          </h2>
          <button className="bulk-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {!result && (
          <>
            <div className="bulk-hint">
              <p>
                Upload an <code>.xlsx</code> with one row per member. Only the{" "}
                <strong>first sheet</strong> is read. Columns (case-insensitive,
                any order):
              </p>
              <ul>
                <li>
                  <strong>name</strong> — full name of the member
                </li>
                <li>
                  <strong>mobile number</strong> — 10-digit phone (also accepts{" "}
                  <em>phone</em>)
                </li>
                <li>
                  <strong>villa number</strong> — <code>Villa-12</code> or just{" "}
                  <code>12</code>
                </li>
                <li>
                  <strong>role</strong> — <em>owner</em>,{" "}
                  <em>shared owner</em>, <em>member</em>, or <em>tenant</em>
                </li>
                <li>
                  <em>email</em> (optional)
                </li>
              </ul>
              <p className="bulk-hint-quiet">
                Members with a duplicate phone number are listed in the Phone
                duplicates section — rerunning with a corrected file is safe.
              </p>
            </div>

            <form onSubmit={submit}>
              <label className="bulk-drop">
                <input
                  type="file"
                  accept=".xlsx"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFile(f);
                    e.target.value = "";
                  }}
                />
                <Upload size={28} strokeWidth={1.6} />
                <p>{file ? file.name : "Click to choose .xlsx"}</p>
                {file && (
                  <span>{(file.size / 1024).toFixed(1)} KB</span>
                )}
              </label>

              {error && <div className="bulk-error">{error}</div>}

              <div className="bulk-actions">
                <button
                  type="button"
                  className="bulk-btn-secondary"
                  onClick={onClose}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bulk-btn-primary"
                  disabled={!file || uploading}
                >
                  {uploading ? "Onboarding…" : "Upload & onboard"}
                </button>
              </div>

              {uploading && (
                <div className="bulk-progress-row">
                  <div className="bulk-progress-bar">
                    <div className="bulk-progress-fill" />
                  </div>
                  <p>
                    Processing rows one by one — please don't close this window…
                  </p>
                </div>
              )}
            </form>
          </>
        )}

        {result && (
          <div className="bulk-result">
            <div className="bulk-summary">
              <div className="bulk-summary-card created">
                <CheckCircle2 size={28} strokeWidth={1.7} />
                <div>
                  <strong>{result.created.length}</strong>
                  <span>Created</span>
                </div>
              </div>
              {(result.duplicates?.length ?? 0) > 0 && (
                <div className="bulk-summary-card duplicates">
                  <PhoneOff size={28} strokeWidth={1.7} />
                  <div>
                    <strong>{result.duplicates.length}</strong>
                    <span>Phone duplicates</span>
                  </div>
                </div>
              )}
              <div className="bulk-summary-card skipped">
                <SkipForward size={28} strokeWidth={1.7} />
                <div>
                  <strong>{result.skipped.length}</strong>
                  <span>Skipped</span>
                </div>
              </div>
              <div className="bulk-summary-card errors">
                <AlertTriangle size={28} strokeWidth={1.7} />
                <div>
                  <strong>{result.errors.length}</strong>
                  <span>Errors</span>
                </div>
              </div>
            </div>

            {result.created.length > 0 && (
              <div className="bulk-section">
                <h3 className="bulk-section-h created">
                  ✓ Created ({result.created.length})
                </h3>
                <ul className="bulk-row-list">
                  {result.created.map((r) => (
                    <li key={`c-${r.row}`}>
                      <span className="bulk-row-n">Row {r.row}</span>
                      <strong>{r.name}</strong>
                      <span className="bulk-row-meta">
                        +91 {r.phone} · {r.villa} · {r.role}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(result.duplicates?.length ?? 0) > 0 && (
              <div className="bulk-section">
                <h3 className="bulk-section-h duplicates">
                  ⚠ Phone duplicates ({result.duplicates.length})
                </h3>
                <ul className="bulk-row-list">
                  {result.duplicates.map((r) => (
                    <li key={`d-${r.row}`}>
                      <span className="bulk-row-n">Row {r.row}</span>
                      <strong>{r.name}</strong>
                      <span className="bulk-row-meta">{r.phone} · {r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.skipped.length > 0 && (
              <div className="bulk-section">
                <h3 className="bulk-section-h skipped">
                  ↷ Skipped ({result.skipped.length})
                </h3>
                <ul className="bulk-row-list">
                  {result.skipped.map((r) => (
                    <li key={`s-${r.row}`}>
                      <span className="bulk-row-n">Row {r.row}</span>
                      <strong>{r.name}</strong>
                      <span className="bulk-row-meta">{r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="bulk-section">
                <h3 className="bulk-section-h errors">
                  ⚠ Errors ({result.errors.length})
                </h3>
                <ul className="bulk-row-list">
                  {result.errors.map((r, i) => (
                    <li key={`e-${i}`}>
                      <span className="bulk-row-n">Row {r.row}</span>
                      <span className="bulk-row-meta error">{r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bulk-actions">
              <button className="bulk-btn-secondary" onClick={reset}>
                Onboard another file
              </button>
              <button className="bulk-btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
